import React, { forwardRef } from 'react';

export interface ChannelCardProps {
  num: string;
  headline: React.ReactNode;
  description: string;
  meta: { k: string; v: React.ReactNode }[];
  ctaLabel: string;
  featured?: boolean;
  onActivate?: () => void;
  href?: string;
}

/**
 * Single contact channel card. Renders as a button when `onActivate` is
 * supplied (for the brief-modal trigger), or as an anchor when `href` is
 * supplied (for mailto links — including the featured "Book a call" card
 * per checker B1 fix).
 *
 * Uses forwardRef so the parent can capture a ref to the activator and
 * restore focus to it after a modal closes.
 */
export const ChannelCard = forwardRef<HTMLButtonElement | HTMLAnchorElement, ChannelCardProps>(
  ({ num, headline, description, meta, ctaLabel, featured, onActivate, href }, ref) => {
    const className = `ai-channel${featured ? ' featured' : ''}`;
    const inner = (
      <>
        <span className="ai-channel-num">{num}</span>
        <h3 className="ai-channel-h3">{headline}</h3>
        <p className="ai-channel-desc">{description}</p>
        <div className="ai-channel-meta">
          {meta.map(m => (
            <div key={m.k}>
              <div className="k">{m.k}</div>
              <div className="v">{m.v}</div>
            </div>
          ))}
        </div>
        <span className="ai-channel-cta">{ctaLabel}</span>
      </>
    );
    if (onActivate) {
      return (
        <button
          type="button"
          ref={ref as React.Ref<HTMLButtonElement>}
          className={className}
          onClick={onActivate}
        >
          {inner}
        </button>
      );
    }
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={className}
        href={href ?? '#'}
      >
        {inner}
      </a>
    );
  }
);
ChannelCard.displayName = 'ChannelCard';

export default ChannelCard;
